import React, { useContext, useMemo } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Table, Heading } from '@frontend/design-system';
import { InsuranceReportContext } from '../dental-insurance-eligibility-report/onederful-insurance-eligibility-report/context/OnederfulReportContext.provider';
import { filterTableData } from '../helpers/get-extras.helper';
import { getDeductiblesTableColConfigs } from '../helpers/table-configs';
import { GridTableEnum } from '../types/tables-data-type';
import { whiteRoundedBg } from './common-styles';
import { GridContainerContext } from './grid-container';
import { SeeMoreOrLessButton } from './see-more-or-less-button';
import { Summary } from './summary';

type deductibleProps = {
  style?: SerializedStyles;
  isPrintingReport?: boolean;
};

export const DeductiblesTable = React.memo(({ style, isPrintingReport = false }: deductibleProps) => {
  const {
    deductibleTableData,
    shouldShowDeductible,
    deductibleSummary,
    filters,
    showNetworkFilter,
    selectedNetworkFilterValue,
  } = useContext(InsuranceReportContext);
  const { selected, setSelected, selectedStyle, notSelectedStyle } = useContext(GridContainerContext);

  const { t } = useTranslation('insuranceEligibilityReport');
  const isSelected = selected === GridTableEnum.DEDUCTIBLE;
  const isNotSelected = !!selected && selected !== GridTableEnum.DEDUCTIBLE;

  const filteredTableData = useMemo(
    () => filterTableData({ data: deductibleTableData, filters, showNetworkFilter, applyFilters: !isPrintingReport }),
    [deductibleTableData, filters, showNetworkFilter]
  );

  const deductiblesTableColConfigs = useMemo(() => getDeductiblesTableColConfigs(isPrintingReport), [isPrintingReport]);

  if (shouldShowDeductible) {
    return (
      <motion.div
        css={[containerStyle, whiteRoundedBg, isSelected && selectedStyle, isNotSelected && notSelectedStyle, style]}
        layout
      >
        <motion.div layout='position' css={headingContainerStyle}>
          <Heading level={2}>{t('Deductibles')}</Heading>
          <SeeMoreOrLessButton
            onClick={() => setSelected(isSelected ? '' : GridTableEnum.DEDUCTIBLE)}
            isSelected={isSelected}
          />
        </motion.div>
        <Summary
          applyFilters={!isPrintingReport}
          data={deductibleSummary ?? []}
          expanded={isSelected}
          noDataFoundMessage={t('No data found for {{selectedNetworkFilterValue}}', { selectedNetworkFilterValue })}
        />
        <Table
          colConfig={deductiblesTableColConfigs}
          data={filteredTableData}
          hasResponsiveColWidths
          isFocusable
          isSelectable={false}
          wrapperStyle={wrapperStyle}
          isPaginated={!isPrintingReport && isSelected && deductibleTableData && deductibleTableData?.length > 0}
        />
      </motion.div>
    );
  }
  return null;
});

DeductiblesTable.displayName = 'DeductiblesTable';

const headingContainerStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const containerStyle = css`
  grid-column: span 2;
`;

const wrapperStyle = css`
  border-bottom-left-radius: ${theme.borderRadius.small};
  border-bottom-right-radius: ${theme.borderRadius.small};
  overflow: hidden;
  background-color: white;
  padding: ${theme.spacing(1, 0, 0, 0)};
`;
